/**
 * Argent du retrait d'une commande en ligne au comptoir : ce qui est déjà
 * encaissé, ce qui reste à encaisser, et comment la vente le ventile.
 *
 * Sorti de CheckoutDialog (05/10/2026) pour être testé sans l'écran : c'est de
 * l'argent, et la ventilation doit toujours retomber sur le total de la vente.
 */

/**
 * Ce qui a RÉELLEMENT été encaissé en ligne (`paid_cents`), pas une
 * soustraction. Repli pour les commandes antérieures à `paid_cents` : l'ancien
 * calcul, plutôt que de réclamer la totalité au client.
 */
export function dejaEncaisse(paidCents: number, totalTtcCents: number, dueCents: number): number {
  return paidCents > 0 ? paidCents : totalTtcCents - dueCents;
}

/**
 * Ce qu'on encaisse au comptoir : rien, sauf ce qui a été AJOUTÉ au panier
 * depuis le chargement de la commande (`baseCents` = la commande telle que
 * chargée). Le surplus de pesée n'est jamais réclamé (règle du 04/09/2026).
 */
export function complementAEncaisser(totalTtcCents: number, baseCents: number, deja: number): number {
  return baseCents > 0 ? Math.max(0, totalTtcCents - baseCents) : Math.max(0, totalTtcCents - deja);
}

export type ModeComplement = 'cash_cents' | 'bancontact_cents' | 'meal_voucher_cents' | 'wero_cents';

/**
 * Jambes du paiement mixte de la remise : la part en ligne, la part FOLIES
 * éventuelle (solde insuffisant = tout le solde), le reste sur le mode choisi.
 */
export function jambesComplement(
  deja: number,
  complement: number,
  foliesCents: number,
  mode: ModeComplement,
): Record<string, number> {
  const folies = Math.max(0, Math.min(foliesCents, complement));
  const jambes: Record<string, number> = { online_cents: deja, [mode]: complement - folies };
  if (folies > 0) jambes.wallet_cents = folies;
  return jambes;
}

/** Les modes qu'on peut combiner pour régler le complément au comptoir. */
export const MODES_COMPLEMENT: ModeComplement[] = ['cash_cents', 'bancontact_cents', 'meal_voucher_cents', 'wero_cents'];

/**
 * Jambes d'un complément réglé avec PLUSIEURS modes (ex. 10 € en espèces,
 * le reste par carte). Même forme que `jambesComplement` : la part en ligne,
 * la part FOLIES éventuelle, puis chaque mode saisi. Les montants nuls ou
 * négatifs sont ignorés : une jambe à 0 n'a rien à faire dans la ventilation.
 */
export function jambesComplementMixte(
  deja: number,
  foliesCents: number,
  parts: Partial<Record<ModeComplement, number>>,
): Record<string, number> {
  const jambes: Record<string, number> = { online_cents: deja };
  if (foliesCents > 0) jambes.wallet_cents = foliesCents;
  for (const mode of MODES_COMPLEMENT) {
    const montant = Math.round(parts[mode] ?? 0);
    if (montant > 0) jambes[mode] = montant;
  }
  return jambes;
}

/** Ce qu'il reste à répartir entre les modes (négatif = trop saisi). */
export function resteAVentiler(
  complement: number,
  foliesCents: number,
  parts: Partial<Record<ModeComplement, number>>,
): number {
  const saisi = MODES_COMPLEMENT.reduce((s, m) => s + Math.max(0, Math.round(parts[m] ?? 0)), 0);
  return complement - Math.max(0, foliesCents) - saisi;
}

/**
 * Retrait d'une commande en ligne déjà payée : ce que le client a déjà réglé
 * et ce qu'il paie AUJOURD'HUI. `null` pour toute autre vente.
 *
 * Une seule règle pour tout ce qui montre un montant au client — dialogue de
 * paiement, écran client, écran « Merci ». Le 10/10/2026, l'écran client
 * affichait le TOTAL du panier (commande en ligne comprise) alors que le
 * client ne devait que les articles ajoutés au comptoir.
 */
export function retraitEnLigne(p: {
  ecomOrderId: string | null;
  ecomOrderPaid: boolean;
  totalTtcCents: number;
  paidCents: number;
  dueCents: number;
  baseCents: number;
}): { dejaPaye: number; aPayer: number } | null {
  if (!p.ecomOrderId || !p.ecomOrderPaid) return null;
  const deja = dejaEncaisse(p.paidCents, p.totalTtcCents, p.dueCents);
  return { dejaPaye: deja, aPayer: complementAEncaisser(p.totalTtcCents, p.baseCents, deja) };
}
